import moment from 'moment'

/**
 * Kody, którymi backend opisuje brak kursu walut przy zapisie/generowaniu delegacji.
 * Wszystkie kończą się tym samym wyjściem awaryjnym (przyjmij ostatni dostępny kurs),
 * ale każdy wymaga innego wyjaśnienia — wcześniej wszystko poza brakiem tabeli
 * wracało jako surowy komunikat HTTP z serwera NBP.
 */
export const CURRENCY_TABLE_ISSUE_CODES = [
  'MISSING_CURRENCY_TABLE',
  'CURRENCY_TABLE_FUTURE_DATE',
  'CURRENCY_SERVICE_UNAVAILABLE',
] as const

export type CurrencyTableIssueCode = (typeof CURRENCY_TABLE_ISSUE_CODES)[number]

export type CurrencyTableIssue = {
  code: CurrencyTableIssueCode
  /** Data kursu w formacie ISO (`YYYY-MM-DD`); pusta, gdy serwer jej nie podał. */
  date: string
}

type ResultLike = {
  code?: string | null
  Code?: string | null
  message?: string | null
  Message?: string | null
}

type Translate = (key: string) => string

const TITLE_KEYS: Record<CurrencyTableIssueCode, string> = {
  MISSING_CURRENCY_TABLE: 'newDelegation.missing-currency-table-title',
  CURRENCY_TABLE_FUTURE_DATE: 'newDelegation.currency-table-future-date-title',
  CURRENCY_SERVICE_UNAVAILABLE:
    'newDelegation.currency-service-unavailable-title',
}

const DETAILS_KEYS: Record<CurrencyTableIssueCode, string> = {
  MISSING_CURRENCY_TABLE: 'newDelegation.missing-currency-table-details-date',
  CURRENCY_TABLE_FUTURE_DATE:
    'newDelegation.currency-table-future-date-details',
  CURRENCY_SERVICE_UNAVAILABLE:
    'newDelegation.currency-service-unavailable-details',
}

export const isCurrencyTableIssueCode = (
  code?: string | null,
): code is CurrencyTableIssueCode =>
  !!code && (CURRENCY_TABLE_ISSUE_CODES as readonly string[]).includes(code)

export const findCurrencyTableIssue = (
  results: ResultLike[] | null | undefined,
): CurrencyTableIssue | null => {
  if (!Array.isArray(results)) return null

  for (const result of results) {
    const code = result?.code ?? result?.Code
    if (isCurrencyTableIssueCode(code)) {
      return { code, date: result?.message ?? result?.Message ?? '' }
    }
  }

  return null
}

type BatchResultLike = ResultLike & {
  DriverId?: number
  driverId?: number
  Success?: boolean
  success?: boolean
}

/**
 * Kierowcy z wsadowego generowania, którym zapis padł na braku kursu. Data kursu jest
 * wspólna dla całego okresu rozliczeniowego, więc zwykle jest to cała paczka i da się
 * ją powtórzyć jednym pytaniem — jak `ForcedExchangeRateDate` w WPF.
 */
export const findCurrencyTableIssueDrivers = (
  results: BatchResultLike[] | null | undefined,
): { issue: CurrencyTableIssue; driverIds: number[] } | null => {
  if (!Array.isArray(results)) return null

  const affected = results.filter(
    (result) =>
      !(result?.Success ?? result?.success) &&
      isCurrencyTableIssueCode(result?.code ?? result?.Code),
  )

  const issue = findCurrencyTableIssue(affected)
  if (!issue) return null

  const driverIds = affected
    .map((result) => result?.DriverId ?? result?.driverId)
    .filter((driverId): driverId is number => typeof driverId === 'number')

  return driverIds.length > 0 ? { issue, driverIds } : null
}

/**
 * Ta sama informacja przychodzi też jako body odpowiedzi 400 (`{ code, message }`)
 * z endpointów, które nie zwracają listy OperationResult - np. aktualizacji kursów.
 */
export const getCurrencyTableIssueFromApiError = (
  error: unknown,
): CurrencyTableIssue | null => {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  if (!data || typeof data !== 'object') return null

  return findCurrencyTableIssue(
    Array.isArray(data) ? (data as ResultLike[]) : [data as ResultLike],
  )
}

/**
 * Backend wysyła datę kursu w ISO; ekrany delegacji pokazują daty jako `DD.MM.YYYY`
 * (patrz `useFormatDate`), więc komunikat trzyma się tej samej konwencji.
 */
export const formatCurrencyTableIssueDate = (date: string): string => {
  if (!date) return ''
  const parsed = moment(date, 'YYYY-MM-DD', true)
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : date
}

export const getCurrencyTableIssueTitle = (
  issue: CurrencyTableIssue,
  t: Translate,
): string => t(TITLE_KEYS[issue.code])

export const getCurrencyTableIssueDetails = (
  issue: CurrencyTableIssue,
  t: Translate,
): string => {
  // Bez daty (starsze odpowiedzi serwera) zostaje samo zdanie opisujące powód -
  // wstawianie pustego miejsca w "na dzień {0}" czytałoby się gorzej niż kod błędu.
  if (!issue.date) return t(`errorCode.${issue.code}`)

  return t(DETAILS_KEYS[issue.code]).replace(
    '{0}',
    formatCurrencyTableIssueDate(issue.date),
  )
}

/** Jednolinijkowy powód do list i podsumowań zadań. */
export const getCurrencyTableIssueReason = (
  issue: CurrencyTableIssue,
  t: Translate,
): string => {
  const reason = t(`errorCode.${issue.code}`)
  const date = formatCurrencyTableIssueDate(issue.date)
  return date ? `${reason} (${date})` : reason
}
